'use strict';

/**
 * Runs -- the graphical consent surface for toolchain runs (Vision 43). One read (GET /1.0/RunQueue via
 * providers.RunAPI) backs the whole page: every run in flight, the steps the platform snapshotted for it,
 * and the evidence a node attached to each step.
 *
 * The point of the page is the gate. When a node reaches a human-gated step it does the preparatory work,
 * attaches a screenshot of what it is about to act on, parks the step at AwaitingConsent, and leaves. The
 * step shows up here with that screenshot inline and the node's note in plain words, and a person with
 * content.approve answers it. Nothing about the decision happens in the browser beyond the answer: approving
 * flips the run back to Running and a later node tick does the work.
 *
 * A toolchain already carrying a run does not offer "Start a run". A run parked at AwaitingConsent is not
 * idle, it is holding that pipeline while it waits for a person, and starting a second run of the same chain
 * is how a node ends up claiming the newer one and walking it past the gate the older one is still sitting
 * at. The card says which run has the chain and what it is waiting on instead (SingleFlightLanes carries the
 * rule, shared with the server that enforces it).
 *
 * Deliberately plain. This is the minimum that makes graphical consent real: read the evidence, approve or
 * reject. A richer run timeline is a follow-on.
 */

const libPictView = require('pict-view');
const libLanes = require('../SingleFlightLanes.js');

// Step statuses that read as finished, so the card can lead with the one that is not.
const _STEP_DONE = { Succeeded: true, Skipped: true, Failed: true };
// Run statuses that read as finished, so polling stops once nothing is moving.
const _RUN_TERMINAL = { Succeeded: true, Failed: true, Cancelled: true };
// Two heartbeat cadences, two stale marks (WI #351 fix B). A worker phase beats every 30s while its node process
// is alive, so a beat older than 90s means those beats stopped -- the node is down. A run-role step parked on the
// deploy lane is different: its heartbeat only refreshes when a node tick re-processes it (~5 min apart), so it is
// quiet between ticks by design. Calling that "stalled" at 90s is the false positive a healthy promote showed. It
// is only really down once even the once-per-tick touch has stopped for well past a tick -- the 12-minute mark the
// server's stuck-run diagnosis already uses (NodeCapability.diagnoseStuckRun).
const _BEAT_STALE_MS = 90000;
const _LANE_STALE_MS = 720000;

class PictViewRuns extends libPictView
{
	_data() { return this.pict.AppData.Runs || {}; }
	_api() { return this.pict.providers.RunAPI; }

	_ms(pValue)
	{
		if (!pValue) { return 0; }
		let tmpString = String(pValue);
		let tmpParsed = Date.parse(tmpString);
		if (!isNaN(tmpParsed)) { return tmpParsed; }
		tmpParsed = Date.parse(tmpString.replace(' ', 'T') + 'Z');
		return isNaN(tmpParsed) ? 0 : tmpParsed;
	}

	_ago(pValue)
	{
		let tmpMs = this._ms(pValue);
		if (!tmpMs) { return ''; }
		let tmpSeconds = Math.floor((Date.now() - tmpMs) / 1000);
		if (tmpSeconds < 0) { tmpSeconds = 0; }
		if (tmpSeconds < 60) { return tmpSeconds + 's ago'; }
		let tmpMinutes = Math.floor(tmpSeconds / 60);
		if (tmpMinutes < 60) { return tmpMinutes + 'm ago'; }
		let tmpHours = Math.floor(tmpMinutes / 60);
		if (tmpHours < 24) { return tmpHours + 'h ago'; }
		return Math.floor(tmpHours / 24) + 'd ago';
	}

	// A compact duration ("45s", "2m", "1h 3m") for elapsed / how-long-it-took. Distinct from _ago (a point in
	// the past): this is a span. Negative or zero reads as 0s.
	_dur(pMs)
	{
		let tmpSeconds = Math.max(0, Math.floor((Number(pMs) || 0) / 1000));
		if (tmpSeconds < 60) { return tmpSeconds + 's'; }
		let tmpMinutes = Math.floor(tmpSeconds / 60);
		if (tmpMinutes < 60) { return tmpMinutes + 'm'; }
		let tmpHours = Math.floor(tmpMinutes / 60);
		let tmpRemMinutes = tmpMinutes % 60;
		return tmpRemMinutes ? (tmpHours + 'h ' + tmpRemMinutes + 'm') : (tmpHours + 'h');
	}

	// The live one-liner under a step: the node's stage + elapsed while it runs, or how long it took once finished.
	// A 0-or-1 slot, so a not-yet-started step stays quiet. A step parked on the deploy lane reads as waiting, not
	// running, and is held to the lane cadence rather than the 90s worker-beat cadence, so it is not called stalled
	// just for being quiet between ticks. is-stalled is set only on a genuine stall, for the CSS to flag (WI #351).
	_stepTiming(pStep)
	{
		let tmpStarted = this._ms(pStep.StartedDate);
		if (pStep.Status === 'Running' && tmpStarted)
		{
			let tmpBeat = this._ms(pStep.HeartbeatDate);
			let tmpSince = tmpBeat ? (Date.now() - tmpBeat) : 0;
			let tmpStage = pStep.StageLabel || '';
			let tmpStalled = '';
			let tmpText;
			if (String(pStep.RefType || '') === 'DeployRequest')
			{
				// Parked on the deploy lane: it beats once per node tick, so it is quiet between ticks by design.
				// Read it as waiting, and only call the node down once even that once-per-tick touch has stopped.
				tmpText = (tmpStage || 'waiting for the deploy lane') + ' - waiting ' + this._dur(Date.now() - tmpStarted);
				if (tmpBeat && tmpSince > _LANE_STALE_MS) { tmpText += ' - no node has touched it in ' + this._dur(tmpSince) + ' (node may be down)'; tmpStalled = ' is-stalled'; }
			}
			else
			{
				// Actively working on the node: a worker phase beats every 30s, so a beat past 90s means it stopped.
				tmpText = (tmpStage ? (tmpStage + ' - ') : '') + 'running ' + this._dur(Date.now() - tmpStarted);
				if (tmpBeat)
				{
					tmpText += ' - beat ' + this._dur(tmpSince) + ' ago';
					if (tmpSince > _BEAT_STALE_MS) { tmpText += ' (node may be stalled)'; tmpStalled = ' is-stalled'; }
				}
			}
			return [{ Timing: this._escape(tmpText), Stalled: tmpStalled }];
		}
		if (_STEP_DONE[pStep.Status] && tmpStarted)
		{
			let tmpFinished = this._ms(pStep.FinishedDate);
			if (tmpFinished && tmpFinished >= tmpStarted) { return [{ Timing: 'took ' + this._dur(tmpFinished - tmpStarted), Stalled: '' }]; }
		}
		return [];
	}

	// Everything this page shows was written by a node or by whoever authored the toolchain, and the
	// templates interpolate raw. On the one surface whose job is to show a person trustworthy evidence
	// before they approve an action, text that can rewrite the page is not acceptable: a log line
	// reading "<button>Approve</button>" would render as one. Escape on the way in.
	_escape(pValue)
	{
		return String(pValue == null ? '' : pValue)
			.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
	}

	_statusClass(pStatus)
	{
		let tmpMap =
		{
			Requested: 'queued', Pending: 'queued', Running: 'running', AwaitingConsent: 'gated',
			Approved: 'gated', Succeeded: 'ok', Failed: 'failed', Cancelled: 'muted', Skipped: 'muted'
		};
		return tmpMap[pStatus] || 'muted';
	}

	// "lands in plansheet (main), may write ./dist, may run git" -- the declared scope in one readable
	// line, so a person approving a step can see what it is allowed to touch without opening the
	// toolchain.
	//
	// Workspaces lead, because they are the part with the largest consequence and the only part the
	// node enforces: approving a step that declares two workspaces is approving a push to two
	// repositories. A person cannot judge that from a line that does not mention it.
	_scopeLine(pScope)
	{
		let tmpScope = pScope || {};
		let tmpParts = [];
		let tmpFS = tmpScope.FS || {};
		let fList = (pArray) => pArray.map((pEntry) => this._escape(pEntry)).join(', ');
		if (Array.isArray(tmpScope.Workspaces) && tmpScope.Workspaces.length)
		{
			tmpParts.push('lands in ' + tmpScope.Workspaces
				.map((pWorkspace) => this._escape(pWorkspace && pWorkspace.Key)
					+ ' (' + this._escape((pWorkspace && pWorkspace.Trunk) || 'main') + ')')
				.join(', '));
		}
		if (Array.isArray(tmpFS.Write) && tmpFS.Write.length) { tmpParts.push('writes ' + fList(tmpFS.Write)); }
		if (Array.isArray(tmpFS.Read) && tmpFS.Read.length) { tmpParts.push('reads ' + fList(tmpFS.Read)); }
		if (Array.isArray(tmpScope.Commands) && tmpScope.Commands.length) { tmpParts.push('runs ' + fList(tmpScope.Commands)); }
		if (Array.isArray(tmpScope.Net) && tmpScope.Net.length) { tmpParts.push('reaches ' + fList(tmpScope.Net)); }
		return tmpParts.length ? ('Scope: ' + tmpParts.join('; ') + '.') : 'Scope: nothing declared.';
	}

	onBeforeRender(pRenderable)
	{
		let tmpD = this._data();
		let tmpCanApprove = !!tmpD.CanApprove;

		// The catalog: what this tenant can run, and what running it will do. A platform entry has not
		// been adopted yet (the first run adopts it); an organization entry is a real tenant row that
		// can be edited. Drifted means the platform edited the built-in after this tenant copied it.
		tmpD.ToolchainCards = (tmpD.Toolchains || []).map((pChain, pIndex) =>
		{
			let tmpGated = Number(pChain.GatedCount) || 0;
			let tmpSteps = Number(pChain.StepCount) || 0;
			// One run of a chain at a time. The runs are already loaded on this page, and the rule for
			// reading them is the same one Run/Start enforces, so the card can answer without another read.
			let tmpInFlight = libLanes.findOpenRunIn(tmpD.Runs || [], 'toolchain:' + String(pChain.Key || ''));
			return {
				Index: pIndex,
				Key: this._escape(pChain.Key || ''),
				Name: this._escape(pChain.Name || pChain.Key || ''),
				Description: this._escape(pChain.Description || ''),
				Tier: this._escape(pChain.Tier || ''),
				TierClass: (pChain.Tier === 'Platform') ? 'queued' : 'running',
				Version: Number(pChain.Version) || 1,
				// Say plainly how much of this a person has to answer for.
				Shape: tmpSteps
					? (tmpSteps + ' step' + (tmpSteps === 1 ? '' : 's') + ', '
						+ (tmpGated ? (tmpGated + ' waiting on you') : 'none waiting on you'))
					: 'no steps yet',
				StepChips: (pChain.Steps || []).map((pStep) => (
				{
					Label: this._escape(pStep.Title || pStep.StepKey || ''),
					Gate: (pStep.GateMode === 'gated') ? 'gated' : 'auto',
					GateClass: (pStep.GateMode === 'gated') ? 'gated' : 'muted'
				})),
				// Which repositories starting this run puts commits into. A slot rather than a string,
				// so a toolchain that declares none renders nothing at all instead of an empty label
				// that would read as an answer.
				WorkspaceSlot: (pChain.Workspaces || []).length
					? [{ Label: 'Lands in ' + pChain.Workspaces
						.map((pWorkspace) => this._escape(pWorkspace.Key) + ' (' + this._escape(pWorkspace.Trunk || 'main') + ')')
						.join(', ') }]
					: [],
				DriftSlot: pChain.Drifted ? [1] : [],
				Runnable: (tmpSteps && !tmpInFlight) ? [{ Index: pIndex }] : [],
				// What has the chain, in the words the server would use if you got past this card and hit
				// the 409 anyway. "Start anyway" is the operator override: it costs content.approve and a
				// written reason, and the run it starts carries a run.forced beat saying it jumped a queue.
				InFlightSlot: tmpInFlight
					? [{
						Label: 'Run #' + (tmpInFlight.Number || tmpInFlight.RunNumber || tmpInFlight.IDRun) + ' is '
							+ libLanes.statusWords(tmpInFlight.Status),
						ForceSlot: tmpCanApprove ? [{ Index: pIndex }] : []
					}]
					: []
			};
		});
		tmpD.ToolchainsEmpty = tmpD.ToolchainCards.length ? [] : [1];
		tmpD.CatalogErrorSlot = tmpD.CatalogError ? [{ Message: this._escape(tmpD.CatalogError) }] : [];

		// How long past runs of each toolchain took, so an in-flight one can say "typically ~4m" and a person
		// knows whether the wait is normal. Median of finished runs' StartedDate->FinishedDate per ToolchainKey.
		let tmpDurations = {};
		(tmpD.Runs || []).forEach((pRun) =>
		{
			if (!_RUN_TERMINAL[pRun.Status || '']) { return; }
			let tmpStartMs = this._ms(pRun.StartedDate);
			let tmpFinishMs = this._ms(pRun.FinishedDate);
			if (tmpStartMs && tmpFinishMs && tmpFinishMs > tmpStartMs)
			{
				(tmpDurations[pRun.ToolchainKey || ''] = tmpDurations[pRun.ToolchainKey || ''] || []).push(tmpFinishMs - tmpStartMs);
			}
		});
		let tmpTypical = {};
		Object.keys(tmpDurations).forEach((pKey) =>
		{
			let tmpSorted = tmpDurations[pKey].sort((pA, pB) => pA - pB);
			tmpTypical[pKey] = tmpSorted[Math.floor(tmpSorted.length / 2)];
		});

		let tmpAllCards = (tmpD.Runs || []).map((pRun) =>
		{
			let tmpSteps = (pRun.Steps || []).map((pStep) =>
			{
				let tmpAwaiting = (pStep.Status === 'AwaitingConsent');
				return {
					IDRunStep: pStep.IDRunStep,
					Order: pStep.SortOrder,
					Title: this._escape(pStep.Title || pStep.StepKey || ''),
					StepKey: this._escape(pStep.StepKey || ''),
					Kind: this._escape(pStep.Kind + (pStep.ToolRef ? (' / ' + pStep.ToolRef) : '')),
					Status: pStep.Status || 'Pending',
					StatusClass: this._statusClass(pStep.Status),
					Gate: (pStep.GateMode === 'gated') ? 'gated' : 'auto',
					// The live one-liner under a running step (stage + elapsed + how fresh the heartbeat is), or
					// how long a finished step took. Empty for a step that has not started (WI #351).
					TimingSlot: this._stepTiming(pStep),
					Ref: pStep.RefType ? this._escape(pStep.RefType + ' ' + pStep.IDRef) : '',
					Log: this._escape(pStep.Log || ''),
					LogSlot: pStep.Log ? [{ Log: this._escape(pStep.Log) }] : [],
					Scope: this._scopeLine(pStep.Scope),
					Evidence: (pStep.Evidence || []).map((pMedia) => ({ IDMedia: Number(pMedia.IDMedia) || 0, Title: this._escape(pMedia.Title || 'Evidence') })),
					// The consent controls appear only on a step that is actually waiting, and only for
					// someone who may answer. A viewer without content.approve sees why instead of a
					// button that would 403.
					ConsentSlot: (tmpAwaiting && tmpCanApprove) ? [{ IDRunStep: pStep.IDRunStep }] : [],
					ConsentDeniedSlot: (tmpAwaiting && !tmpCanApprove) ? [1] : [],
					DecisionSlot: pStep.ConsentDecision
						? [{ Decision: this._escape(pStep.ConsentDecision), Note: this._escape(pStep.ConsentNote || '') }]
						: []
				};
			});

			let tmpNext = tmpSteps.filter((pStep) => !_STEP_DONE[pStep.Status])[0];
			let tmpRunTerminal = !!_RUN_TERMINAL[pRun.Status || ''];
			let tmpRunStartedMs = this._ms(pRun.StartedDate);
			let tmpRunFinishedMs = this._ms(pRun.FinishedDate);
			// Lowercased haystack for the client-side filter over finished runs (number, name, toolchain, status).
			let tmpSearch = this._escape(('run #' + (pRun.Number || '') + ' ' + (pRun.Name || '') + ' '
				+ (pRun.ToolchainKey || '') + ' ' + (pRun.Status || '')).toLowerCase());
			return {
				IDRun: pRun.IDRun,
				Number: pRun.Number,
				Name: this._escape(pRun.Name || ''),
				ToolchainKey: this._escape(pRun.ToolchainKey || ''),
				Status: pRun.Status || '',
				StatusClass: this._statusClass(pRun.Status),
				// A finished run reads by when it finished plus how long it took ("finished 56m ago · took 4m"),
				// which answers "how long did the last ones take"; an in-flight one reads by when it started.
				Ago: tmpRunTerminal
					? ('finished ' + this._ago(pRun.FinishedDate || pRun.CreateDate) + ((tmpRunStartedMs && tmpRunFinishedMs) ? (' · took ' + this._dur(tmpRunFinishedMs - tmpRunStartedMs)) : ''))
					: this._ago(pRun.StartedDate || pRun.CreateDate),
				// Live run-level elapsed while it is moving ("running 2m"). Empty once the run is finished.
				Elapsed: (!tmpRunTerminal && tmpRunStartedMs) ? ('running ' + this._dur(Date.now() - tmpRunStartedMs)) : '',
				// How long this toolchain usually takes, shown only while running so a person knows what to wait for.
				TypicalSlot: (!tmpRunTerminal && tmpTypical[pRun.ToolchainKey || '']) ? [{ Typical: 'typically ~' + this._dur(tmpTypical[pRun.ToolchainKey || '']) }] : [],
				NodeHost: this._escape(pRun.NodeHost || ''),
				Position: tmpNext
					? ('at step ' + tmpNext.Order + ' of ' + tmpSteps.length + ': ' + tmpNext.Title)
					: (tmpSteps.length + ' of ' + tmpSteps.length + ' steps done'),
				// Why a run is sitting unclaimed, from the server (which node could take it and what is missing).
				// Only for an in-flight run; the server only fills it once the run has actually waited a while.
				WaitingSlot: (pRun.WaitingReason && !tmpRunTerminal) ? [{ Reason: this._escape(pRun.WaitingReason) }] : [],
				// Stop a run going wrong, without SSH-ing into the node (Work Item #451). Only offered on a
				// Running run to someone who could answer a gate -- the server gates the route the same way.
				// A cancel already requested but not yet noticed shows as "stopping" instead of a second button,
				// so a slow heartbeat does not read as the click having done nothing.
				CancelSlot: (pRun.Status === 'Running' && tmpCanApprove && !pRun.CancelRequested) ? [{ IDRun: pRun.IDRun }] : [],
				StoppingSlot: (pRun.Status === 'Running' && pRun.CancelRequested) ? [1] : [],
				Steps: tmpSteps,
				// Fields the split/sort below reads; the underscored ones are internal and no template references them.
				Terminal: tmpRunTerminal,
				Search: tmpSearch,
				_StartedMs: tmpRunStartedMs,
				_FinishedMs: tmpRunFinishedMs,
				_Gated: (pRun.Status === 'AwaitingConsent')
			};
		});

		// Active runs (anything not finished) lead the page, above the catalog: a gated run needs you and a running
		// one is what you came to watch, so neither should sit below a scroll of toolchains and history. A run
		// awaiting consent sorts to the very top; otherwise most-recently-started first.
		tmpD.ActiveRunCards = tmpAllCards.filter((pCard) => !pCard.Terminal)
			.sort((pA, pB) => (pA._Gated === pB._Gated) ? (pB._StartedMs - pA._StartedMs) : (pA._Gated ? -1 : 1));
		// Finished runs collapse into a compact, searchable list, newest first; each row expands in place for its
		// steps and logs. This keeps the page scannable once dozens of runs have piled up.
		tmpD.FinishedRunCards = tmpAllCards.filter((pCard) => pCard.Terminal)
			.sort((pA, pB) => (pB._FinishedMs || 0) - (pA._FinishedMs || 0));
		if (typeof tmpD.FinishedFilter !== 'string') { tmpD.FinishedFilter = ''; }
		tmpD.ActiveTitleSlot = tmpD.ActiveRunCards.length ? [{ Count: tmpD.ActiveRunCards.length }] : [];
		tmpD.FinishedTitleSlot = tmpD.FinishedRunCards.length ? [{ Filter: tmpD.FinishedFilter }] : [];
		tmpD.RunsEmpty = (tmpD.ActiveRunCards.length + tmpD.FinishedRunCards.length) ? [] : [1];
		tmpD.LoadErrorSlot = tmpD.LoadError ? [{ Message: tmpD.LoadError }] : [];

		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		// A poll-driven re-render repaints the finished list from scratch; re-apply the filter so hidden rows stay
		// hidden and the box keeps working mid-typing-session while a run is live.
		if (typeof document !== 'undefined')
		{
			let tmpInput = document.querySelector('.ps-runs-search');
			if (tmpInput && tmpInput.value) { this.filterFinished(tmpInput.value); }
		}
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	// Expand or collapse a finished run's detail in place. The compact list stays scannable, but any run's steps
	// and logs are one click away without leaving the page.
	toggleRun(pElement)
	{
		if (typeof document === 'undefined' || !pElement || !pElement.closest) { return; }
		let tmpWrap = pElement.closest('.ps-runs-mini-wrap');
		if (tmpWrap) { tmpWrap.classList.toggle('is-open'); }
	}

	// Client-side filter over the finished-run rows: hide any whose search text does not contain the query. Pure
	// DOM, so typing never re-renders the page (which would drop the input's focus) and makes no server traffic.
	// The raw query is stashed on AppData so a re-render restores the box value and onAfterRender re-applies it.
	filterFinished(pQuery)
	{
		if (typeof document === 'undefined') { return; }
		let tmpRaw = String(pQuery == null ? '' : pQuery);
		this._data().FinishedFilter = this._escape(tmpRaw);
		let tmpQuery = tmpRaw.trim().toLowerCase();
		let tmpRows = document.querySelectorAll('.ps-runs-mini-wrap');
		for (let i = 0; i < tmpRows.length; i++)
		{
			let tmpText = String(tmpRows[i].getAttribute('data-search') || '');
			tmpRows[i].style.display = (!tmpQuery || tmpText.indexOf(tmpQuery) >= 0) ? '' : 'none';
		}
	}

	// Entry point from the router: load, paint, and keep it live while a run is moving.
	show() { let tmpPromise = this.refresh(); this._ensurePolling(); return tmpPromise; }

	refresh()
	{
		let tmpAPI = this._api();
		if (!tmpAPI || typeof tmpAPI.loadRuns !== 'function') { this.render(); return Promise.resolve(); }
		// A transient fetch failure must not blank the pane: each loader resolves with its own error
		// parked on AppData, and this catch covers anything the render itself throws. The catalog and
		// the runs load together so one slow read does not paint the page twice.
		return Promise.all([tmpAPI.loadRuns(), tmpAPI.loadToolchains()])
			.then(() => { this.render(); this._ensurePolling(); }).catch(() => this.render());
	}

	// Poll while a run is in flight so stage / elapsed / log tick without a manual Refresh. Self-terminating:
	// it stops the moment you navigate off the Runs route or nothing is moving, and a fresh show()/refresh()
	// re-arms it. One timer only, and it re-reads only when a run is active, so a settled board makes no
	// traffic. setInterval (not requestAnimationFrame) so it keeps firing even when the tab is backgrounded.
	_onRunsPage() { return (typeof window !== 'undefined' && window.location && /^#?\/?runs\b/.test(String(window.location.hash || ''))); }
	_hasActiveRun() { return ((this._data().Runs) || []).some((pRun) => !_RUN_TERMINAL[pRun.Status || '']); }
	_ensurePolling()
	{
		if (typeof window === 'undefined' || this._pollTimer) { return; }
		if (!this._onRunsPage() || !this._hasActiveRun()) { return; }
		let tmpSelf = this;
		this._pollTimer = setInterval(function ()
		{
			if (!tmpSelf._onRunsPage() || !tmpSelf._hasActiveRun()) { tmpSelf._stopPolling(); return; }
			tmpSelf.refresh();
		}, 5000);
	}
	_stopPolling() { if (this._pollTimer) { clearInterval(this._pollTimer); this._pollTimer = null; } }
}

module.exports = PictViewRuns;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-Runs',
	DefaultRenderable: 'Plansheet-Runs',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,
	CSS: /*css*/`
		.ps-runs-head { display: flex; align-items: center; gap: 14px; margin-bottom: 16px; }
		.ps-runs-head h1 { font-size: 22px; font-weight: 600; margin: 0; flex: 1; color: var(--theme-color-text-primary, #2c3140); }
		.ps-runs-btn { padding: 7px 14px; font-size: 13px; cursor: pointer; border: 1px solid var(--theme-color-border-default, #d8dde6);
			border-radius: 8px; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #2c3140); }
		.ps-runs-btn:hover { border-color: var(--theme-color-brand-primary, #3b6fd4); }
		.ps-runs-err { background: var(--theme-color-status-error, #cc3333); color: #fff; padding: 8px 12px; border-radius: 8px; margin-bottom: 14px; font-size: 13px; }
		.ps-runs-empty { color: var(--theme-color-text-secondary, #8a93a6); font-style: italic; font-size: 13px; padding: 10px 2px; }
		.ps-runs-section-title { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em;
			color: var(--theme-color-text-secondary, #5b6376); margin: 22px 0 10px; }
		.ps-runs-section-title:first-of-type { margin-top: 4px; }
		.ps-runs-catalog { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 12px; }
		.ps-runs-chain { border: 1px solid var(--theme-color-border-light, #e7ecf0); border-radius: 12px; padding: 14px 16px;
			background: var(--theme-color-background-panel, #fff); display: flex; flex-direction: column; gap: 7px; }
		.ps-runs-chain-head { display: flex; gap: 8px; align-items: baseline; flex-wrap: wrap; }
		.ps-runs-chain-name { flex: 1; min-width: 0; font-size: 14px; font-weight: 600; color: var(--theme-color-text-primary, #2c3140); }
		.ps-runs-chain-key { font-size: 11px; color: var(--theme-color-text-secondary, #8a93a6);
			font-family: ui-monospace, SFMono-Regular, Menlo, monospace; margin-top: -4px; }
		.ps-runs-chain-desc { font-size: 12px; line-height: 1.5; color: var(--theme-color-text-secondary, #5b6376); margin: 0; }
		.ps-runs-chain-shape { font-size: 12px; font-weight: 600; color: var(--theme-color-text-primary, #2c3140); }
		.ps-runs-chain-workspaces { font-size: 12px; color: var(--theme-color-text-secondary, #5b6376); margin-top: 2px;
			font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
		.ps-runs-chips { display: flex; flex-wrap: wrap; gap: 6px; }
		.ps-runs-chip { font-size: 11px; padding: 3px 8px; border-radius: 6px; display: inline-flex; gap: 5px; align-items: baseline;
			background: color-mix(in srgb, #8a93a6 10%, transparent); color: var(--theme-color-text-secondary, #5b6376); }
		.ps-runs-chip em { font-style: normal; font-size: 10px; opacity: 0.75; text-transform: uppercase; letter-spacing: 0.03em; }
		.ps-runs-chip.gated { background: color-mix(in srgb, #d99a2b 18%, transparent); color: #a06d12; }
		.ps-runs-start { align-self: flex-start; margin-top: 3px; }
		/* A chain that already has a run: a live status, not a greyed-out button. */
		.ps-runs-chain-inflight { display: flex; align-items: center; gap: 10px; margin-top: 3px; font-size: 12px;
			color: #a06d12; background: color-mix(in srgb, #d99a2b 12%, transparent);
			border: 1px solid color-mix(in srgb, #d99a2b 40%, transparent); border-radius: 8px; padding: 6px 10px; }
		.ps-runs-anyway { font-size: 11px; padding: 3px 9px; cursor: pointer; border-radius: 6px;
			border: 1px solid var(--theme-color-border-default, #d8dde6); background: transparent;
			color: var(--theme-color-text-secondary, #5b6376); }
		.ps-runs-anyway:hover { border-color: var(--theme-color-text-secondary, #5b6376); }
		.ps-runs-card { border: 1px solid var(--theme-color-border-light, #e7ecf0); border-radius: 12px; margin-bottom: 16px;
			background: var(--theme-color-background-panel, #fff); overflow: hidden; }
		.ps-runs-card-head { display: flex; gap: 10px; align-items: baseline; padding: 12px 16px;
			border-bottom: 1px solid var(--theme-color-border-light, #e7ecf0); }
		.ps-runs-num { font-weight: 700; font-size: 13px; color: var(--theme-color-text-secondary, #8a93a6); }
		.ps-runs-name { flex: 1; min-width: 0; font-size: 14px; font-weight: 600; color: var(--theme-color-text-primary, #2c3140);
			white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
		.ps-runs-meta { font-size: 11px; color: var(--theme-color-text-secondary, #8a93a6); }
		.ps-runs-position { padding: 6px 16px; font-size: 12px; color: var(--theme-color-text-secondary, #5b6376);
			background: color-mix(in srgb, var(--theme-color-text-secondary, #8a93a6) 5%, transparent); }
		.ps-runs-step { display: flex; flex-direction: column; gap: 6px; padding: 11px 16px; border-top: 1px solid var(--theme-color-border-light, #e7ecf0); }
		.ps-runs-step-line { display: flex; gap: 10px; align-items: baseline; }
		.ps-runs-step-order { flex: 0 0 auto; font-size: 12px; font-weight: 700; color: var(--theme-color-text-secondary, #8a93a6); min-width: 18px; }
		.ps-runs-step-title { flex: 1; min-width: 0; font-size: 13px; color: var(--theme-color-text-primary, #2c3140); }
		.ps-runs-step-kind { flex: 0 0 auto; font-size: 11px; color: var(--theme-color-text-secondary, #8a93a6);
			font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
		.ps-runs-badge { flex: 0 0 auto; font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 999px;
			text-transform: uppercase; letter-spacing: 0.03em; }
		.ps-runs-badge.queued { background: color-mix(in srgb, #8a93a6 14%, transparent); color: #5b6376; }
		.ps-runs-badge.running { background: color-mix(in srgb, var(--theme-color-brand-primary, #3b6fd4) 15%, transparent); color: var(--theme-color-brand-primary, #3b6fd4); }
		.ps-runs-badge.gated { background: color-mix(in srgb, #d99a2b 20%, transparent); color: #a06d12; }
		.ps-runs-badge.ok { background: color-mix(in srgb, var(--theme-color-status-success, #3fae5a) 16%, transparent); color: #2f8a46; }
		.ps-runs-badge.failed { background: color-mix(in srgb, var(--theme-color-status-error, #cc3333) 15%, transparent); color: var(--theme-color-status-error, #cc3333); }
		.ps-runs-badge.muted { background: color-mix(in srgb, #8a93a6 12%, transparent); color: #8a93a6; }
		.ps-runs-scope { font-size: 11px; color: var(--theme-color-text-secondary, #8a93a6); padding-left: 28px; }
		/* The live progress line under a step (WI #351): stage + elapsed + heartbeat freshness, or how long it took. */
		.ps-runs-timing { font-size: 11.5px; color: var(--theme-color-text-secondary, #5b6376); padding-left: 28px; margin-top: 2px; font-variant-numeric: tabular-nums; }
		.ps-runs-timing.is-stalled { color: var(--theme-color-status-warning, #b9812f); font-weight: 600; }
		/* Run-level "running Nm" + "typically ~Nm" on the position line. */
		.ps-runs-elapsed { margin-left: 8px; font-variant-numeric: tabular-nums; }
		.ps-runs-elapsed:empty { display: none; }
		.ps-runs-typical { margin-left: 8px; color: var(--theme-color-text-secondary, #8a93a6); }
		.ps-runs-waiting { margin: 2px 16px 10px; padding: 8px 11px; font-size: 12px; line-height: 1.5; border-radius: 8px; color: var(--theme-color-status-warning, #b9812f); background: rgba(185, 129, 47, 0.10); border: 1px solid rgba(185, 129, 47, 0.5); }
		.ps-runs-waiting-tag { display: inline-block; margin-right: 8px; font-weight: 700; text-transform: uppercase; font-size: 10px; letter-spacing: 0.05em; }
		/* Stop a run going wrong, without SSH-ing into the node (WI #451). */
		.ps-runs-cancel { padding: 4px 11px; font-size: 12px; font-weight: 600; cursor: pointer; border-radius: 7px;
			border: 1px solid var(--theme-color-status-error, #cc3333); background: transparent; color: var(--theme-color-status-error, #cc3333); }
		.ps-runs-cancel:hover { background: color-mix(in srgb, var(--theme-color-status-error, #cc3333) 10%, transparent); }
		.ps-runs-stopping { font-size: 12px; font-style: italic; color: var(--theme-color-text-secondary, #8a93a6); }
		.ps-runs-log { white-space: pre-wrap; font-size: 12px; line-height: 1.45; color: var(--theme-color-text-secondary, #5b6376);
			background: color-mix(in srgb, var(--theme-color-text-secondary, #8a93a6) 6%, transparent);
			border-radius: 8px; padding: 8px 10px; margin: 0 0 0 28px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
		.ps-runs-evidence { display: flex; flex-wrap: wrap; gap: 10px; padding-left: 28px; }
		.ps-runs-evidence figure { margin: 0; }
		.ps-runs-evidence img { max-width: 420px; width: 100%; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px; display: block; }
		.ps-runs-evidence figcaption { font-size: 11px; color: var(--theme-color-text-secondary, #8a93a6); margin-top: 3px; }
		.ps-runs-consent { display: flex; gap: 10px; align-items: center; padding-left: 28px; }
		.ps-runs-consent-note { flex: 1; font-size: 12px; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-runs-approve { padding: 6px 14px; font-size: 13px; font-weight: 600; cursor: pointer; border-radius: 8px;
			border: 1px solid var(--theme-color-status-success, #3fae5a); background: var(--theme-color-status-success, #3fae5a); color: #fff; }
		.ps-runs-reject { padding: 6px 14px; font-size: 13px; cursor: pointer; border-radius: 8px;
			border: 1px solid var(--theme-color-status-error, #cc3333); background: transparent; color: var(--theme-color-status-error, #cc3333); }
		.ps-runs-decision { font-size: 12px; color: var(--theme-color-text-secondary, #5b6376); padding-left: 28px; }
		/* Recent-runs header with its inline filter box. */
		.ps-runs-finished-head { display: flex; align-items: center; gap: 12px; margin: 22px 0 10px; flex-wrap: wrap; }
		.ps-runs-finished-head .ps-runs-section-title { margin: 0; flex: 0 0 auto; }
		.ps-runs-search { flex: 1; min-width: 180px; max-width: 340px; padding: 6px 10px; font-size: 12px; border-radius: 8px;
			border: 1px solid var(--theme-color-border-default, #d8dde6); background: var(--theme-color-background-panel, #fff);
			color: var(--theme-color-text-primary, #2c3140); }
		.ps-runs-search:focus { outline: none; border-color: var(--theme-color-brand-primary, #3b6fd4); }
		/* A finished run: one compact row, expandable in place. */
		.ps-runs-mini-wrap { border: 1px solid var(--theme-color-border-light, #e7ecf0); border-radius: 10px; margin-bottom: 8px;
			background: var(--theme-color-background-panel, #fff); overflow: hidden; }
		.ps-runs-mini { display: flex; gap: 10px; align-items: baseline; padding: 9px 14px; cursor: pointer; }
		.ps-runs-mini:hover { background: color-mix(in srgb, var(--theme-color-brand-primary, #3b6fd4) 5%, transparent); }
		.ps-runs-mini-caret { flex: 0 0 auto; font-size: 10px; color: var(--theme-color-text-secondary, #8a93a6); transition: transform 0.12s; align-self: center; }
		.ps-runs-mini-wrap.is-open .ps-runs-mini-caret { transform: rotate(90deg); }
		.ps-runs-mini-name { flex: 1; min-width: 0; font-size: 13px; font-weight: 600; color: var(--theme-color-text-primary, #2c3140);
			white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
		.ps-runs-mini-chain { flex: 0 0 auto; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
		.ps-runs-mini-ago { flex: 0 0 auto; }
		.ps-runs-mini-body { display: none; border-top: 1px solid var(--theme-color-border-light, #e7ecf0); padding-bottom: 6px; }
		.ps-runs-mini-wrap.is-open .ps-runs-mini-body { display: block; }
	`,
	Templates:
	[
		{
			Hash: 'Plansheet-Runs',
			Template: /*html*/`
<div class="ps-page ps-runs">
	<div class="ps-runs-head">
		<h1>Runs</h1>
		<button class="ps-runs-btn" onclick="_Pict.views['Plansheet-Runs'].refresh()">Refresh</button>
	</div>

	{~TS:Runs-LoadError:AppData.Runs.LoadErrorSlot~}
	{~TS:Runs-ActiveTitle:AppData.Runs.ActiveTitleSlot~}
	{~TS:Runs-Card:AppData.Runs.ActiveRunCards~}

	<p class="ps-runs-section-title">Toolchains you can run</p>
	{~TS:Runs-CatalogError:AppData.Runs.CatalogErrorSlot~}
	{~TS:Runs-ToolchainsEmpty:AppData.Runs.ToolchainsEmpty~}
	<div class="ps-runs-catalog">{~TS:Runs-Toolchain:AppData.Runs.ToolchainCards~}</div>

	{~TS:Runs-FinishedTitle:AppData.Runs.FinishedTitleSlot~}
	{~TS:Runs-Mini:AppData.Runs.FinishedRunCards~}
	{~TS:Runs-Empty:AppData.Runs.RunsEmpty~}
</div>`
		},
		{
			Hash: 'Runs-Card',
			Template: /*html*/`
<div class="ps-runs-card">
	<div class="ps-runs-card-head">
		<span class="ps-runs-num">Run #{~D:Record.Number~}</span>
		<span class="ps-runs-name">{~D:Record.Name~}</span>
		<span class="ps-runs-meta">{~D:Record.ToolchainKey~}</span>
		<span class="ps-runs-meta">{~D:Record.Ago~}</span>
		<span class="ps-runs-badge {~D:Record.StatusClass~}">{~D:Record.Status~}</span>
		{~TS:Runs-Cancel:Record.CancelSlot~}
		{~TS:Runs-Stopping:Record.StoppingSlot~}
	</div>
	<div class="ps-runs-position">{~D:Record.Position~}<span class="ps-runs-elapsed">{~D:Record.Elapsed~}</span>{~TS:Runs-Typical:Record.TypicalSlot~}</div>
	{~TS:Runs-Waiting:Record.WaitingSlot~}
	{~TS:Runs-Step:Record.Steps~}
</div>`
		},
		{
			Hash: 'Runs-Step',
			Template: /*html*/`
<div class="ps-runs-step">
	<div class="ps-runs-step-line">
		<span class="ps-runs-step-order">{~D:Record.Order~}</span>
		<span class="ps-runs-step-title">{~D:Record.Title~}</span>
		<span class="ps-runs-step-kind">{~D:Record.Kind~} &middot; {~D:Record.Gate~}</span>
		<span class="ps-runs-badge {~D:Record.StatusClass~}">{~D:Record.Status~}</span>
	</div>
	{~TS:Runs-Step-Timing:Record.TimingSlot~}
	<div class="ps-runs-scope">{~D:Record.Scope~} {~D:Record.Ref~}</div>
	{~TS:Runs-Log:Record.LogSlot~}
	<div class="ps-runs-evidence">{~TS:Runs-Evidence:Record.Evidence~}</div>
	{~TS:Runs-Consent:Record.ConsentSlot~}
	{~TS:Runs-ConsentDenied:Record.ConsentDeniedSlot~}
	{~TS:Runs-Decision:Record.DecisionSlot~}
</div>`
		},
		{ Hash: 'Runs-Log', Template: /*html*/`<pre class="ps-runs-log">{~D:Record.Log~}</pre>` },
		{ Hash: 'Runs-Step-Timing', Template: /*html*/`<div class="ps-runs-timing{~D:Record.Stalled~}">{~D:Record.Timing~}</div>` },
		{ Hash: 'Runs-Typical', Template: /*html*/`<span class="ps-runs-typical">{~D:Record.Typical~}</span>` },
		{ Hash: 'Runs-Waiting', Template: /*html*/`<div class="ps-runs-waiting"><span class="ps-runs-waiting-tag">Waiting on a node</span>{~D:Record.Reason~}</div>` },
		// Stop a run going wrong, without SSH-ing into the node (Work Item #451). Takes IDRun, not a
		// string: nothing tenant-authored ever reaches this onclick, but an integer is the same safe
		// shape the rest of the page already uses for its onclick handlers.
		{ Hash: 'Runs-Cancel', Template: /*html*/`<button class="ps-runs-cancel" onclick="_Pict.PictApplication.cancelRun({~D:Record.IDRun~})">Stop</button>` },
		{ Hash: 'Runs-Stopping', Template: /*html*/`<span class="ps-runs-stopping">stopping&hellip;</span>` },
		{
			Hash: 'Runs-Evidence',
			Template: /*html*/`<figure><img src="/1.0/Media/{~D:Record.IDMedia~}/Blob" alt="{~D:Record.Title~}"><figcaption>{~D:Record.Title~}</figcaption></figure>`
		},
		{
			Hash: 'Runs-Consent',
			Template: /*html*/`
<div class="ps-runs-consent">
	<span class="ps-runs-consent-note">This step is waiting on you. Read the evidence above, then decide.</span>
	<button class="ps-runs-reject" onclick="_Pict.PictApplication.consentRunStep({~D:Record.IDRunStep~}, 'Reject')">Reject</button>
	<button class="ps-runs-approve" onclick="_Pict.PictApplication.consentRunStep({~D:Record.IDRunStep~}, 'Approve')">Approve</button>
</div>`
		},
		{ Hash: 'Runs-ConsentDenied', Template: /*html*/`<div class="ps-runs-consent"><span class="ps-runs-consent-note">This step is waiting for someone who can approve. You do not have content.approve.</span></div>` },
		{ Hash: 'Runs-Decision', Template: /*html*/`<div class="ps-runs-decision">{~D:Record.Decision~} by a person. {~D:Record.Note~}</div>` },
		{ Hash: 'Runs-LoadError', Template: /*html*/`<div class="ps-runs-err">Could not load runs: {~D:Record.Message~}</div>` },
		{ Hash: 'Runs-CatalogError', Template: /*html*/`<div class="ps-runs-err">Could not load the toolchain catalog: {~D:Record.Message~}</div>` },
		{ Hash: 'Runs-Empty', Template: /*html*/`<p class="ps-runs-empty">No runs yet. Start one from a toolchain above and a node will walk it, bringing the gated steps here for you to approve.</p>` },
		{ Hash: 'Runs-ActiveTitle', Template: /*html*/`<p class="ps-runs-section-title">Active now ({~D:Record.Count~})</p>` },
		{
			Hash: 'Runs-FinishedTitle',
			Template: /*html*/`
<div class="ps-runs-finished-head">
	<p class="ps-runs-section-title">Recent runs</p>
	<input class="ps-runs-search" type="search" placeholder="Filter by number, name, or toolchain" value="{~D:Record.Filter~}" oninput="_Pict.views['Plansheet-Runs'].filterFinished(this.value)">
</div>`
		},
		// A finished run is a compact row that expands in place; clicking the header toggles is-open on the wrapper.
		// The full detail (steps + logs) reuses Runs-Step, so an expanded row reads exactly like an active card.
		{
			Hash: 'Runs-Mini',
			Template: /*html*/`
<div class="ps-runs-mini-wrap" data-search="{~D:Record.Search~}">
	<div class="ps-runs-mini" onclick="_Pict.views['Plansheet-Runs'].toggleRun(this)">
		<span class="ps-runs-mini-caret">&#9656;</span>
		<span class="ps-runs-num">Run #{~D:Record.Number~}</span>
		<span class="ps-runs-mini-name">{~D:Record.Name~}</span>
		<span class="ps-runs-meta ps-runs-mini-chain">{~D:Record.ToolchainKey~}</span>
		<span class="ps-runs-badge {~D:Record.StatusClass~}">{~D:Record.Status~}</span>
		<span class="ps-runs-meta ps-runs-mini-ago">{~D:Record.Ago~}</span>
	</div>
	<div class="ps-runs-mini-body">
		<div class="ps-runs-position">{~D:Record.Position~}</div>
		{~TS:Runs-Step:Record.Steps~}
	</div>
</div>`
		},
		{ Hash: 'Runs-ToolchainsEmpty', Template: /*html*/`<p class="ps-runs-empty">No toolchains are available. The platform built-ins seed at boot; if this stays empty, the seed did not run.</p>` },
		{
			Hash: 'Runs-Toolchain',
			Template: /*html*/`
<div class="ps-runs-chain">
	<div class="ps-runs-chain-head">
		<span class="ps-runs-chain-name">{~D:Record.Name~}</span>
		<span class="ps-runs-badge {~D:Record.TierClass~}">{~D:Record.Tier~}</span>
		<span class="ps-runs-meta">v{~D:Record.Version~}</span>
		{~TS:Runs-Drift:Record.DriftSlot~}
	</div>
	<div class="ps-runs-chain-key">{~D:Record.Key~}</div>
	<p class="ps-runs-chain-desc">{~D:Record.Description~}</p>
	<div class="ps-runs-chain-shape">{~D:Record.Shape~}</div>
	{~TS:Runs-ChainWorkspaces:Record.WorkspaceSlot~}
	<div class="ps-runs-chips">{~TS:Runs-StepChip:Record.StepChips~}</div>
	{~TS:Runs-StartChain:Record.Runnable~}
	{~TS:Runs-ChainInFlight:Record.InFlightSlot~}
</div>`
		},
		{ Hash: 'Runs-ChainWorkspaces', Template: /*html*/`<div class="ps-runs-chain-workspaces">{~D:Record.Label~}</div>` },
		{ Hash: 'Runs-StepChip', Template: /*html*/`<span class="ps-runs-chip {~D:Record.GateClass~}">{~D:Record.Label~}<em>{~D:Record.Gate~}</em></span>` },
		{ Hash: 'Runs-Drift', Template: /*html*/`<span class="ps-runs-badge gated" title="The platform built-in changed after this copy was taken">drifted</span>` },
		// The handler takes the catalog INDEX, not the key or name. A tenant-authored key interpolated
		// into this onclick would be inside a JS string inside an HTML attribute, and HTML-escaping is
		// the wrong escape for that nesting: the parser decodes &#39; back to a quote before JS sees
		// it, so an escaped quote would break out of the literal. An integer cannot.
		{ Hash: 'Runs-StartChain', Template: /*html*/`<button class="ps-runs-btn ps-runs-start" onclick="_Pict.PictApplication.startToolchainRun({~D:Record.Index~})">Start a run</button>` },
		// The chain is busy, so there is no Start button to press. The label is a plain statement of what has
		// it; the run itself is on a card further down this same page.
		{
			Hash: 'Runs-ChainInFlight',
			Template: /*html*/`<div class="ps-runs-chain-inflight"><span>{~D:Record.Label~}</span>{~TS:Runs-StartChainAnyway:Record.ForceSlot~}</div>`
		},
		// Same index-not-key rule as Runs-StartChain above: an integer cannot break out of the JS string
		// literal inside the HTML attribute, and a tenant-authored toolchain key could.
		{ Hash: 'Runs-StartChainAnyway', Template: /*html*/`<button class="ps-runs-anyway" onclick="_Pict.PictApplication.startToolchainRunAnyway({~D:Record.Index~})">Start anyway</button>` }
	],
	Renderables:
	[
		{
			RenderableHash: 'Plansheet-Runs',
			TemplateHash: 'Plansheet-Runs',
			ContentDestinationAddress: '#Plansheet-Workspace',
			RenderMethod: 'replace'
		}
	]
};
